import { useIntl, defineMessages } from 'react-intl';
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import { MediaProps } from '@designsystem/stories/02-atoms/Media';
import {
  Intro,
  IntroProps as DSIntroProps,
} from '@designsystem/stories/04-organisms/Intro';
import type { ButtonGroupProps } from '@designsystem/stories/03-molecules/ButtonGroup';
import { Info } from '@designsystem/stories/02-atoms/Info';
import { Leri } from '@designsystem/stories/04-organisms/Leri';
import { RichText } from '../RichText';
import { normalizeLink } from '@fhnw/helpers/DesignSystem/normalizeContent';
import {
  NormalizableContent,
  transformToCard,
} from '@fhnw/helpers/DesignSystem/cardTransformers';

const messages = defineMessages({
  Contact: {
    id: 'intro_contact',
    defaultMessage: 'Contact',
  },
  Room: {
    id: 'intro_room',
    defaultMessage: 'Room',
  },
  Availability: {
    id: 'intro_availability',
    defaultMessage: 'Availability',
  },
  AdditionalInformation: {
    id: 'intro_additional_information',
    defaultMessage: 'Additional information',
  },
  Role: {
    id: 'intro_role',
    defaultMessage: 'Job title/activity of the person',
  },
  Address: {
    id: 'intro_address',
    defaultMessage: 'Address',
  },
});

const ContactIntro = (props: DSIntroProps & { content?: Content }) => {
  const intl = useIntl();
  const { title, breadcrumb, content, prevLabel, nextLabel, swipeLabel } =
    props;

  const normalized = content
    ? transformToCard(content as NormalizableContent, intl, 'person')
    : undefined;
  const slideMedia: MediaProps[] | undefined = props?.slides?.length
    ? props.slides
        .filter((slide) => slide?.media)
        .map(
          (slide): MediaProps => ({
            ...slide.media,
            focus_point: slide.media?.focus_point || 'center_right',
            // The format an editor picked wins, as it does on the gallery
            // block. Without one, keep the landscape crop of a person page.
            aspect: slide.media?.aspect || 'landscape',
          }),
        )
    : undefined;
  let image: MediaProps | undefined = undefined;
  if (slideMedia?.length) {
    image = slideMedia[0];
  } else if (!normalized?.image?.className?.includes('media--placeholder')) {
    image = normalized?.image;
  }
  const info = normalized?.info;

  const actions: ButtonGroupProps['items'] = (normalized?.actions || []).map(
    (action) => ({
      ...action,
      label: action.title,
      title: '',
      size: 'medium',
    }),
  );

  if (content?.additional_information) {
    actions.push({
      href: normalizeLink(content?.additional_information),
      label: intl.formatMessage(messages.AdditionalInformation),
      type: 'secondary',
      target: '_blank',
    });
  }

  const address = `${content?.location_address?.address || ''}\n${content?.location_address?.zipcode || ''} ${content?.location_address?.city || ''}`;

  return (
    <>
      <Intro
        title={title}
        breadcrumb={breadcrumb}
        stickyActions={{
          actions: actions
            .reverse()
            .map((action) => ({ ...action, icon: undefined })),
        }}
      />
      <div className="page none-ui">
        <section className="page__section surface--invert page__section--intro-contact">
          {content?.role && (
            <div className="page__section-intro">
              <p className="text--lead">
                <strong>{intl.formatMessage(messages.Role)}</strong>
                <br />
                <RichText text={content?.role} />
              </p>
            </div>
          )}
          <div className="page__section-content">
            <Leri
              media={image}
              slides={slideMedia}
              prevLabel={prevLabel}
              nextLabel={nextLabel}
              swipeLabel={swipeLabel}
            >
              {(info || address.trim()) && (
                <>
                  <h4>{intl.formatMessage(messages.Contact)}</h4>
                  <Info items={info} />
                  {address.trim() && (
                    <>
                      <h4>{intl.formatMessage(messages.Address)}</h4>
                      <p>
                        <RichText text={address} />
                      </p>
                    </>
                  )}
                </>
              )}
              {content?.room && (
                <>
                  <h4>{intl.formatMessage(messages.Room)}</h4>
                  <p>
                    <RichText text={content?.room} />
                  </p>
                </>
              )}
              {content?.availability && (
                <>
                  <h4>{intl.formatMessage(messages.Availability)}</h4>
                  <p>
                    <RichText text={content?.availability} />
                  </p>
                </>
              )}
            </Leri>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactIntro;
